import React, { useState, useEffect } from 'react';
import { activityApi } from '../utils/api';
import { getImageUrl } from '../config/imageConfig';
import CarouselBanner from './common/CarouselBanner';
import './Events.css';

// 默认活动数据（作为备用）
const getDefaultEvents = (eventImg) => [
  {
    id: 1,
    title: '药用植物讲座：认识常见药草',
    categoryName: '学术讲座',
    date: '2024年12月15日',
    time: '14:30 - 16:00',
    location: '科研展示厅',
    image: eventImg,
    description: '由研究所资深研究员主讲，介绍常见药用植物的识别和应用。活动包括现场植物展示和互动问答环节。',
  },
  {
    id: 2,
    title: '秋季药草采集指南',
    categoryName: '野外活动',
    date: '2024年12月21日',
    time: '9:00 - 12:00',
    location: '四季药园',
    image: eventImg,
    description: '学习如何在秋季识别和适当采集药用植物，包括采集技巧和可持续实践。',
  },
  {
    id: 3,
    title: '中药香囊制作工坊',
    categoryName: '亲子活动',
    date: '2024年12月28日',
    time: '10:00 - 11:30',
    location: '创意工作室',
    image: eventImg,
    description: '亲子手工活动，学习制作传统中药香囊，了解药材的功效和香囊文化。',
  }
];

function Events() {
  // API数据状态
  const [categories, setCategories] = useState([]);
  const [eventsList, setEventsList] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationLoading, setRegistrationLoading] = useState({});
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  
  // 图片资源
  const eventsHeroImg = getImageUrl('https://images.unsplash.com/photo-1530103862676-de8c9debad1d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80');
  const event1Img = getImageUrl('https://images.unsplash.com/photo-1578986568072-9a3cf310dab4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80');

  // 获取活动分类
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await activityApi.getCategories();
        if (response.code === 200) {
          setCategories(response.data || []);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);
  
  // 获取统计信息
  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        const statsRes = await activityApi.getStatistics();
        if (statsRes.code === 200) {
          setStatistics(statsRes.data);
        }
      } catch (err) {
        console.error('Error fetching statistics:', err);
      }
    };
    fetchStatistics();
  }, []);
  
  // 获取活动列表
  useEffect(() => {
    const fetchEventsList = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const params = {
          page: currentPage,
          size: pageSize,
          sortBy: 'startDate',
          orderBy: 'asc'
        };
        
        if (selectedCategory) {
          params.categoryId = selectedCategory;
        }
        
        const response = await activityApi.getList(params);
        
        if (response && response.code === 200 && response.data) {
          const records = response.data.records || [];
          setEventsList(records.length > 0 ? records : getDefaultEvents(event1Img));
          setTotalPages(response.data.pages || 1);
        } else {
          setEventsList(getDefaultEvents(event1Img));
        }
      } catch (err) {
        console.error('Error fetching events list:', err);
        setError('加载活动列表失败');
        setEventsList(getDefaultEvents(event1Img));
      } finally {
        setLoading(false);
      }
    };
    
    fetchEventsList();
  }, [currentPage, pageSize, selectedCategory, event1Img]);
  
  // 活动报名处理
  const handleRegisterEvent = async (eventId) => {
    try {
      setRegistrationLoading(prev => ({ ...prev, [eventId]: true }));
      
      const registrationData = {
        participantName: '访客',
        participantPhone: '13800138000',
        participantEmail: 'visitor@example.com',
        participantsCount: 1
      };
      
      const response = await activityApi.register(eventId, registrationData);
      
      if (response && response.code === 200) {
        alert(`报名成功！报名编号：${response.data?.registrationCode || '成功'}`);
      } else {
        alert(response?.message || '报名失败，请稍后重试');
      }
    } catch (err) {
      console.error('Error registering event:', err);
      alert('报名失败，请稍后重试');
    } finally {
      setRegistrationLoading(prev => ({ ...prev, [eventId]: false }));
    }
  };
  
  // 查看活动详情
  const handleViewDetail = async (eventId) => {
    try {
      await activityApi.addView(eventId);
      window.location.href = `/events/${eventId}`;
    } catch (err) {
      console.error('Error viewing event detail:', err);
    }
  };
  
  // 处理分类选择
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };
  
  // 格式化日期显示
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}年${month}月${day}日`;
    } catch {
      return dateString;
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <CarouselBanner 
        categoryNameEn="JQHD"
        defaultImage={eventsHeroImg}
        defaultTitle="活动中心"
        defaultSubtitle="探索庐山植物园丰富的教育和娱乐活动，了解更多关于药用植物和植物科学知识"
        className="hero-section"
      />
      
      <div className="events-container">
        {/* 活动分类筛选 */}
        {categories.length > 0 && (
          <div className="categories-filter">
            <h3>活动分类</h3>
            <div className="category-buttons">
              <button 
                className={`category-btn ${!selectedCategory ? 'active' : ''}`}
                onClick={() => handleCategorySelect(null)}
              >
                全部活动
              </button>
              {categories.map(category => (
                <button 
                  key={category.id}
                  className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => handleCategorySelect(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* 统计信息 */}
        {statistics && (
          <div className="statistics-bar">
            <div className="stat-item">
              <span className="stat-label">活动总数</span>
              <span className="stat-value">{statistics.totalActivities || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">即将开始</span>
              <span className="stat-value">{statistics.upcomingActivities || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">进行中</span>
              <span className="stat-value">{statistics.ongoingActivities || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">累计参与</span>
              <span className="stat-value">{statistics.totalParticipants || 0}人</span>
            </div>
          </div>
        )}
        
        {/* 加载状态 */}
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner">加载中...</div>
          </div>
        )}
        
        {/* 错误提示 */}
        {error && !loading && (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>重试</button>
          </div>
        )}
        
        {/* 活动列表 */}
        {!loading && (
          <div className="events-grid">
            {eventsList.length > 0 ? (
              eventsList.map((event) => (
                <div key={event.id} className="event-card">
                  <img 
                    src={event.featuredImage || event.featured_image_url || event.image || event1Img} 
                    alt={event.title} 
                    onError={(e) => { e.target.src = event1Img; }}
                  />
                  <div className="event-content">
                    <span className="event-type">
                      {event.categoryName || event.category?.name || event.type || '活动'}
                    </span>
                    <h3>{event.title}</h3>
                    <p className="event-description">
                      {event.description || event.summary || '欢迎参加我们的活动，了解更多植物科学知识。'}
                    </p>
                    <div className="event-meta">
                      <span className="event-date">
                        {event.start_date ? formatDate(event.start_date) : event.date || '待定'}
                      </span>
                      <span className="event-time">
                        {event.time || '14:30 - 16:00'}
                      </span>
                      {event.location && (
                        <span className="event-location">
                          📍 {event.location}
                        </span>
                      )}
                    </div>
                    <div className="event-actions">
                      <button 
                        className="btn-learn-more"
                        onClick={() => handleViewDetail(event.id)}
                      >
                        了解更多
                      </button>
                      <button 
                        className="btn-register-now"
                        onClick={() => handleRegisterEvent(event.id)}
                        disabled={registrationLoading[event.id]}
                      >
                        {registrationLoading[event.id] ? '处理中...' : '立即报名'}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-events">
                <p>暂无活动信息</p>
              </div>
            )}
          </div>
        )}
        
        {/* 分页器 */}
        {!loading && totalPages > 1 && (
          <div className="pagination">
            <button 
              className="page-btn"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              上一页
            </button>
            <span className="page-info">
              第 {currentPage} / {totalPages} 页
            </span>
            <button 
              className="page-btn"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Events;
